import { graphql } from 'gatsby';
import { Layout, Seo } from '@layout';
import { Box } from '@components/Box';
import { BabyRegistry } from '@sections/shared/BabyRegistry';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useOnScreenTelemetry } from '@hooks/useOnScreenTelemetry';
import { findACouplePageData } from '@data-ts/pages/find-an-event';
import FindACoupleContainer from '@sections/find-an-event/searchResponse';
import { AllEventTypes, EventType } from '@sections/find/HeroSection/HeroSection.types';
import { ABExperimentName, experiments } from '@utils/experiments/experiments';
import { useExperimentsTelemetry } from '@utils/experiments/useExperimentsTelemetry';
import { HeroSection, BlogCarouselV2, CelebratingCoupleSection, NavigationSection, BrandsSection, BlogPropsV2 } from '@sections/find';

const BABY_REGISTRY_SECTION_ID = 'FindAnEvent_Baby_Registry_Section';

interface FindCouplePageData {
  weddingGuestsBlogs: {
    blogs: ReadonlyArray<BlogPropsV2>;
  };
  babyGuestsBlogs: {
    blogs: ReadonlyArray<BlogPropsV2>;
  };
}

export const query = graphql`
  {
    weddingGuestsBlogs {
      id
      blogs {
        link
        title
        excerpt
        mediaLink
        categories {
          id
          name
          link
        }
      }
    }
    babyGuestsBlogs {
      id
      blogs {
        link
        title
        excerpt
        mediaLink
        categories {
          id
          name
          link
        }
      }
    }
  }
`;

const FindCouple = ({ data }: { data: FindCouplePageData }) => {
  const [eventType, setEventType] = useState<AllEventTypes>('');
  const babyRegistrySectionRef = useRef<HTMLDivElement>(null);

  const FindAnEventExperimentControl = experiments.FindAnEventExperiment.Control.Component;
  const FindAnEventExperimentVariant = experiments.FindAnEventExperiment.Variant.Component;

  useOnScreenTelemetry(babyRegistrySectionRef, BABY_REGISTRY_SECTION_ID);
  useExperimentsTelemetry({ name: ABExperimentName.FindAnEventExperiment });

  const blogs = useMemo(() => (eventType === EventType.babyRegistry ? data.babyGuestsBlogs.blogs : data.weddingGuestsBlogs.blogs), [
    data.babyGuestsBlogs.blogs,
    data.weddingGuestsBlogs.blogs,
    eventType,
  ]);

  const handleOnChangeEventType = useCallback((eventType: AllEventTypes) => setEventType(eventType), []);

  return (
    <Layout
      isTransparentHeader={true}
      transparentJoyLogoColor="newPurple5"
      transparentHeaderButtonVariant="primary"
      transparentHeaderLinkVariant="navLink"
      page="find"
    >
      <Seo title={findACouplePageData.seo.title} description={findACouplePageData.seo.content} canonicalURL={findACouplePageData.seo.url} />
      <FindAnEventExperimentControl>
        <FindACoupleContainer />
      </FindAnEventExperimentControl>
      <FindAnEventExperimentVariant>
        <HeroSection onChangeEventType={handleOnChangeEventType} eventType={eventType} />
        <BlogCarouselV2
          key={eventType}
          sectionId="FindAnEvent_Blog_Section"
          title={findACouplePageData.blogTitle}
          blogs={blogs.slice(0, 9)}
        />
        <CelebratingCoupleSection />
        <NavigationSection />
        <BrandsSection />
        <Box as="section" id={BABY_REGISTRY_SECTION_ID} ref={babyRegistrySectionRef}>
          <BabyRegistry page="find" />
        </Box>
      </FindAnEventExperimentVariant>
    </Layout>
  );
};

export default FindCouple;
